// The clickable row of a table (DataTable's onRowClick, and the hand-built tables that open a
// summary the same way): a click anywhere on the row opens it, except a click on a control of its
// own inside a cell (a link, a button, a switch, an inline form), which is left to that control. Keyboard and
// screen-reader users open the row with its visually hidden `.row-open` button in the first cell
// (a `role="button"` row would nest the cells' links: axe `nested-interactive`). No preact here:
// the identity screens shared with web/home import this too.

/** What a click inside a clickable row may land on that is not the row itself. */
const OWN_CONTROL = "a[href], button, input, select, textarea, summary, label, form, [role=button], [role=switch], [role=checkbox]";

/** What a dialog's focus trap cycles through (ui/dialog.tsx, and the row summary shared with web/home). */
export const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The class of a row's open button; the row's click handler focuses it before opening. */
export const ROW_OPEN_CLASS = "row-open";

/** A click on the row itself (or on its open button), not on a control inside one of its cells. */
export function isRowClick(target: EventTarget | null, row: Element): boolean {
  if (!(target instanceof Element)) return true;
  const control = target.closest(OWN_CONTROL);
  return control === null || !row.contains(control) || control.classList.contains(ROW_OPEN_CLASS);
}

/**
 * The row's click handler: on a row click, focus the row's open button (so the dialog gives focus
 * back to this row when it closes) and open it.
 */
export function rowClickHandler(open: () => void): (event: MouseEvent) => void {
  return (event) => {
    const row = event.currentTarget;
    if (!(row instanceof Element) || !isRowClick(event.target, row)) return;
    row.querySelector<HTMLElement>(`.${ROW_OPEN_CLASS}`)?.focus({ preventScroll: true });
    open();
  };
}
